import React, { createContext, useContext, useReducer } from 'react';
import apiClient from '../config/axios';

// Initial state
const initialState = {
  //   users: [],
  courses: [],
  //   members: [],
  //   payments: [],
  //   membershipList: [],
  enrollments: [],
  stats: {
    totalCourses: 0,
  },
  loading: false,
  error: null,
};

// Action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_DATA: 'SET_DATA',
  //   UPDATE_COURSES: 'UPDATE_COURSES',
  UPDATE_ENROLLMENTS: 'UPDATE_ENROLLMENTS',
  UPDATE_SINGLE_ENROLLMENT: 'UPDATE_SINGLE_ENROLLMENT', // Aggiungi questa action
  ADD_PAYMENT: 'ADD_PAYMENT',
  UPDATE_ATTENDANCE: 'UPDATE_ATTENDANCE',
  CREATE_ATTENDANCE: 'CREATE_ATTENDANCE',
  ADD_COURSE_DATE: 'ADD_COURSE_DATE',
  REMOVE_COURSE_DATE: 'REMOVE_COURSE_DATE',
};

// Reducer
const teacherReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };

    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };

    case ACTIONS.SET_DATA:
      return {
        ...state,
        courses: action.payload.courses || [],
        enrollments: action.payload.enrollments || [],
        stats: {
          totalCourses: action.payload.courses?.length || 0,
        },
      };

    // case ACTIONS.UPDATE_COURSES:
    //   return {
    //     ...state,
    //     courses: action.payload,
    //     stats: { ...state.stats, totalCourses: action.payload.length },
    //   };

    case ACTIONS.UPDATE_ENROLLMENTS:
      return { ...state, enrollments: action.payload };

    case ACTIONS.UPDATE_SINGLE_ENROLLMENT:
      return {
        ...state,
        enrollments: state.enrollments.map((enrollment) => (enrollment._id === action.payload._id ? action.payload : enrollment)),
      };

    case ACTIONS.UPDATE_ATTENDANCE:
      return {
        ...state,
        enrollments: state.enrollments.map((enrollment) => {
          if (enrollment._id === action.payload.enrollmentId) {
            return {
              ...enrollment,
              attendances: action.payload.attendances,
            };
          }
          return enrollment;
        }),
      };

    case ACTIONS.CREATE_ATTENDANCE:
      return {
        ...state,
        enrollments: state.enrollments.map((enrollment) => {
          if (enrollment._id === action.payload.enrollmentId) {
            return {
              ...enrollment,
              attendances: action.payload.attendances,
            };
          }
          return enrollment;
        }),
      };

    case ACTIONS.ADD_COURSE_DATE:
      return {
        ...state,
        courses: state.courses.map((course) => {
          if (course._id === action.payload.courseId) {
            return {
              ...course,
              dates: action.payload.dates,
            };
          }
          return course;
        }),
      };

    case ACTIONS.REMOVE_COURSE_DATE:
      return {
        ...state,
        courses: state.courses.map((course) => {
          if (course._id === action.payload.courseId) {
            return {
              ...course,
              dates: action.payload.dates,
            };
          }
          return course;
        }),
      };

    default:
      return state;
  }
};

// Context
const TeacherContext = createContext();

// Provider
export const TeacherProvider = ({ children }) => {
  const [state, dispatch] = useReducer(teacherReducer, initialState);

  // Fetch all data
  const fetchAllData = async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const [coursesRes, enrollmentsRes] = await Promise.all([apiClient.get('/api/courses'), apiClient.get('/api/enrollments')]);

      dispatch({
        type: ACTIONS.SET_DATA,
        payload: {
          courses: coursesRes.data,
          enrollments: enrollmentsRes.data,
        },
      });
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore nel caricamento dei dati' });
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  const addPayment = async (paymentData) => {
    try {
      await apiClient.post('/api/payments', paymentData);

      // Invece di ricaricare tutti gli enrollments, aggiorna solo quello specifico
      const enrollmentRes = await apiClient.get(`/api/enrollments/${paymentData.enrollmentId}`);

      return { success: true, data: enrollmentRes.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante la creazione pagamento' });
      return { success: false, error: error.message };
    }
  };

  const setAttendance = async (enrollmentId, date, status) => {
    try {
      const response = await apiClient.put(`/api/enrollments/${enrollmentId}/attendance`, { date, status });

      return { success: true, data: response.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante il salvataggio presenza' });
      return { success: false, error: error.message };
    }
  };

  const removeAttendance = async (enrollmentId, date) => {
    try {
      const response = await apiClient.delete(`/api/enrollments/${enrollmentId}/attendance`, { data: { date } });

      return { success: true, data: response.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante la rimozione presenza' });
      return { success: false, error: error.message };
    }
  };

  const getCourseById = async (courseId) => {
    try {
      const response = await apiClient.get(`/api/courses/${courseId}`);
      return { success: true, data: response.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante il caricamento del corso' });
      return { success: false, error: error.message };
    }
  };

  const getEnrollmentsByCourseId = async (courseId) => {
    try {
      const response = await apiClient.get('/api/enrollments', {
        params: { courseId, includeAttendances: true },
      });
      return { success: true, data: response.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante il caricamento delle iscrizioni' });
      return { success: false, error: error.message };
    }
  };

  const removeCourseDate = async (courseId, date) => {
    try {
      await apiClient.delete(`/api/courses/${courseId}/date`, { data: { date } });
      // Ottieni le nuove date dal backend (ipotizziamo che le ritorni in response.data.dates)
      const courseRes = await apiClient.get(`/api/courses/${courseId}`);
      dispatch({
        type: ACTIONS.REMOVE_COURSE_DATE,
        payload: { courseId, dates: courseRes.data.dates },
      });
      return { success: true };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante la rimozione della data' });
      return { success: false, error: error.message };
    }
  };

  const addCourseDate = async (courseId, date) => {
    try {
      await apiClient.post(`/api/courses/${courseId}/date`, { date });
      // Ottieni le nuove date dal backend (ipotizziamo che le ritorni in response.data.dates)
      const courseRes = await apiClient.get(`/api/courses/${courseId}`);
      dispatch({
        type: ACTIONS.ADD_COURSE_DATE,
        payload: { courseId, dates: courseRes.data.dates },
      });
      return { success: true };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: "Errore durante l'aggiunta della data" });
      return { success: false, error: error.message };
    }
  };

  const refreshData = () => {
    fetchAllData();
  };

  const clearError = () => {
    dispatch({ type: ACTIONS.SET_ERROR, payload: null });
  };

  // Course file methods
  const uploadCourseFile = async (courseId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(`/api/files/courses/${courseId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return { success: true, data: response.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante il caricamento del file' });
      return { success: false, error: error.message };
    }
  };

  const getCourseFiles = async (courseId) => {
    try {
      const response = await apiClient.get(`/api/files/courses/${courseId}`);
      return { success: true, data: response.data };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante il caricamento dei file del corso' });
      return { success: false, error: error.message };
    }
  };

  const deleteCourseFile = async (courseId, fileKey) => {
    try {
      const encodedFileKey = encodeURIComponent(fileKey);
      await apiClient.delete(`/api/files/courses/${courseId}/${encodedFileKey}`);
      return { success: true };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante l\'eliminazione del file' });
      return { success: false, error: error.message };
    }
  };

  const downloadCourseFile = async (courseId, fileKey) => {
    try {
      const encodedFileKey = encodeURIComponent(fileKey);
      const response = await apiClient.get(`/api/files/courses/${courseId}/download/${encodedFileKey}`, {
        responseType: 'blob',
      });
      
      // Create blob and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileKey.split('/').pop(); // Extract filename from fileKey
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      return { success: true };
    } catch (error) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Errore durante il download del file' });
      return { success: false, error: error.message };
    }
  };

  const value = {
    ...state,
    // updateUser,
    // updateCourse,
    // deleteCourse,
    // addCourse,
    // addEnrollment,
    addPayment,
    // addMember,
    // updateMember,
    // addMembershipList,
    // updateMembershipListItem,
    // deleteEnrollment,
    setAttendance,
    removeAttendance,
    getCourseById,
    getEnrollmentsByCourseId,
    removeCourseDate,
    addCourseDate,
    uploadCourseFile,
    getCourseFiles,
    deleteCourseFile,
    downloadCourseFile,
    refreshData,
    clearError,
  };

  return <TeacherContext.Provider value={value}>{children}</TeacherContext.Provider>;
};

// Hook
export const useTeacher = () => {
  const context = useContext(TeacherContext);
  if (!context) {
    throw new Error('useTeacher must be used within a TeacherProvider');
  }
  return context;
};
